import Icon from 'atoms/Icons/Icon';
import color from 'atoms/colors/colors';

// Quarks
import Flex from 'quarks/Flex';
import Image, { ImageData } from 'quarks/Image';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';

import richTextParser from 'utils/richTextParser';

// Types
import type { ContentfulComponentTestimonial } from 'graphqlTypes';
import type { FC } from 'react';

interface TestimonialCardProps extends FlexProps, ContentfulComponentTestimonial {
  backgroundIsDark: boolean;
}

const textWrapCSS = `
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TestimonialCard: FC<TestimonialCardProps> = ({
  logo,
  logoBackgroundColor,
  person,
  testimonial,
  backgroundIsDark,
  ...props
}) => (
  <Flex
    position="relative"
    width="100%"
    flexDirection="column"
    borderRadius="16px"
    overflowX="hidden"
    overflowY="hidden"
    backgroundColor={backgroundIsDark ? 'gray-900' : 'navy-900'}
    md={{ flexDirection: 'row' }}
    {...props}
  >
    <Flex
      width="100%"
      position="relative"
      flexDirection="column"
      justifyContent="center"
      paddingAll={16}
      gap="24px"
      height="100%"
      maxHeight="fit-content"
      md={{ gap: '48px', paddingAll: 40 }}
    >
      <Icon id="quote" fill={color.green[300]} size={40} />
      {testimonial && (
        <Flex
          textColor="common-white"
          fontFamily="secondaryFont"
          fontSize="textMd"
          lineHeight="textMd"
          md={{ fontSize: 'textLg', lineHeight: 'textLg' }}
          css={textWrapCSS}
        >
          {richTextParser(testimonial)}
        </Flex>
      )}
      <Flex
        flexDirection="column"
        justifyContent="space-between"
        gap="24px"
        sm={{ flexDirection: 'row', alignItems: 'center' }}
      >
        {person && (
          <Flex flexDirection="column" gap="4px">
            {person?.name && (
              <Text
                textColor="common-white"
                textStyle="textSm"
                fontWeight="bold"
                md={{ textStyle: 'textLg', fontWeight: 'bold' }}
              >
                {person.name}
              </Text>
            )}
            {person?.position && (
              <Text textColor="common-white" textStyle="textSm" md={{ textStyle: 'textMd' }}>
                {person.position}
              </Text>
            )}
          </Flex>
        )}
        {logo && logo?.__typename === 'ContentfulEntityCompany' && (
          <Flex
            width="137px"
            height="69px"
            paddingAll={logoBackgroundColor?.value ? 16 : 0}
            borderRadius="8px"
            css={`
              background: ${logoBackgroundColor?.value || 'transparent'};
            `}
          >
            <Image
              image={logo?.logo as ImageData}
              alt="Testimonial Card Image"
              objectFit="contain"
              objectPosition="center"
              width="100%"
              height="100%"
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  </Flex>
);

export default TestimonialCard;
